import { TableIds, useTableStore } from "@/store/tables";
import { ColumnDef, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { useMemo } from "react";

interface UseGetFlatRowsTableProps<T> {
  data: T[];
  columns: ColumnDef<T>[];
  tableId: TableIds;
}
const UseGetFlatRowsTable = <T>({ data, columns, tableId }: UseGetFlatRowsTableProps<T>) => {
  const { getTableSettings, setTableSettings } = useTableStore();
  const savedSettings = getTableSettings(tableId);
  const columnOrder =
    savedSettings.columnOrder.length > 0 ? savedSettings.columnOrder : columns.map((c) => c.id!);

  const table = useReactTable({
    data: data || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    columnResizeMode: "onChange",
    onColumnOrderChange: (updater) => {
      setTableSettings(tableId, {
        ...savedSettings,
        columnOrder: updater instanceof Function ? updater(savedSettings.columnOrder) : updater,
      });
    },
    defaultColumn: {
      minSize: 150,
      maxSize: 600,
    },
    state: {
      columnOrder,
    },
  });

  const info = table.getState().columnSizingInfo;
  const size = table.getState().columnSizing;
  const headers = table.getFlatHeaders();
  const columnVisibility = savedSettings.columnVisibility;
  const columnSizeVars = useMemo(() => {
    const colSizes: { [key: string]: number } = {};
    for (let i = 0; i < headers.length; i++) {
      const header = headers[i]!;
      colSizes[`--header-${header.id}-size`] = header.getSize();
      colSizes[`--col-${header.column.id}-size`] = header.column.getSize();
    }
    return colSizes;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [info, size, columnVisibility, columnOrder, headers]);

  return { table, columnOrder, columnSizeVars };
};

export default UseGetFlatRowsTable;
